<template>
  <div class="policy-container">
    <header class="policy-header">
      <h1>{{ t("policy.title") }}</h1>
      <p>{{ t("policy.lastUpdated", { date: currentDate }) }}</p>
    </header>

    <div class="policy-content">
      <!-- 目录导航 -->
      <nav class="policy-nav">
        <h3>{{ t("policy.toc") }}</h3>
        <ul>
          <li v-for="section in sections" :key="section.id">
            <a
              :href="`#${section.id}`"
              @click.prevent="scrollToSection(section.id)"
            >
              {{ t(`policy.sections.${section.id}`) }}
            </a>
          </li>
        </ul>
      </nav>

      <!-- 主要内容 -->
      <div class="policy-main">
        <!-- 导言 -->
        <section id="introduction" class="policy-section">
          <h2>{{ t("policy.sections.introduction") }}</h2>
          <p>{{ t("policy.introduction.welcome") }}</p>

          <div class="important-note">
            <h4>{{ t("policy.introduction.importantNote") }}</h4>
            <p>{{ t("policy.introduction.agreementText") }}</p>
          </div>
        </section>

        <!-- 用户使用协议 -->
        <section id="userAgreement" class="policy-section">
          <h2>{{ t("policy.sections.userAgreement") }}</h2>

          <div class="subsection">
            <h3>{{ t("policy.userAgreement.serviceDescription.title") }}</h3>
            <p>{{ t("policy.userAgreement.serviceDescription.content") }}</p>
            <ul>
              <li
                v-for="(feature, index) in tm(
                  'policy.userAgreement.serviceDescription.features'
                )"
                :key="index"
              >
                {{ feature }}
              </li>
            </ul>
          </div>
          <div class="subsection">
            <h3>{{ t("policy.userAgreement.paidSubscriptions.title") }}</h3>
            <ul>
              <li
                v-for="(point, index) in tm(
                  'policy.userAgreement.paidSubscriptions.points'
                )"
                :key="index"
              >
                {{ point }}
              </li>
            </ul>
          </div>
          <div class="subsection">
            <h3>{{ t("policy.userAgreement.userEligibility.title") }}</h3>
            <ul>
              <li
                v-for="(point, index) in tm(
                  'policy.userAgreement.userEligibility.points'
                )"
                :key="index"
              >
                {{ point }}
              </li>
            </ul>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.userAgreement.userContent.title") }}</h3>
            <ul>
              <li
                v-for="(point, index) in tm(
                  'policy.userAgreement.userContent.points'
                )"
                :key="index"
              >
                {{ point }}
              </li>
            </ul>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.userAgreement.disclaimer.title") }}</h3>
            <ul>
              <li
                v-for="(point, index) in tm(
                  'policy.userAgreement.disclaimer.points'
                )"
                :key="index"
              >
                {{ point }}
              </li>
            </ul>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.userAgreement.serviceChanges.title") }}</h3>
            <p>{{ t("policy.userAgreement.serviceChanges.content") }}</p>
          </div>
        </section>

        <!-- 隐私权政策 -->
        <section id="privacyPolicy" class="policy-section">
          <h2>{{ t("policy.sections.privacyPolicy") }}</h2>
          <p>{{ t("policy.privacyPolicy.intro") }}</p>

          <div class="subsection">
            <h3>{{ t("policy.privacyPolicy.informationCollection.title") }}</h3>
            <ul>
              <li
                v-for="(point, index) in tm(
                  'policy.privacyPolicy.informationCollection.points'
                )"
                :key="index"
              >
                {{ point }}
              </li>
            </ul>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.privacyPolicy.informationUsage.title") }}</h3>
            <p>{{ t("policy.privacyPolicy.informationUsage.content") }}</p>
            <ul>
              <li
                v-for="(point, index) in tm(
                  'policy.privacyPolicy.informationUsage.points'
                )"
                :key="index"
              >
                {{ point }}
              </li>
            </ul>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.privacyPolicy.informationSharing.title") }}</h3>
            <p>{{ t("policy.privacyPolicy.informationSharing.content") }}</p>
            <ul>
              <li
                v-for="(point, index) in tm(
                  'policy.privacyPolicy.informationSharing.points'
                )"
                :key="index"
              >
                <span v-if="index === 3">
                  {{ point }}
                  <div class="external-links">
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                    >
                      {{
                        t(
                          "policy.privacyPolicy.informationSharing.links.google"
                        )
                      }}
                    </a>
                    <a
                      href="https://www.facebook.com/policy.php"
                      target="_blank"
                    >
                      {{
                        t("policy.privacyPolicy.informationSharing.links.meta")
                      }}
                    </a>
                  </div>
                </span>
                <template v-else>{{ point }}</template>
              </li>
            </ul>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.privacyPolicy.dataSecurity.title") }}</h3>
            <p>{{ t("policy.privacyPolicy.dataSecurity.content") }}</p>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.privacyPolicy.yourRights.title") }}</h3>
            <p>{{ t("policy.privacyPolicy.yourRights.content") }}</p>
            <ul>
              <li
                v-for="(point, index) in tm(
                  'policy.privacyPolicy.yourRights.points'
                )"
                :key="index"
              >
                {{ point }}
              </li>
            </ul>
            <p>
              {{
                t(
                  "policy.privacyPolicy.yourRights.contact",
                  "deeptech853@gmail.com"
                )
              }}
            </p>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.privacyPolicy.dataRetention.title") }}</h3>
            <p>{{ t("policy.privacyPolicy.dataRetention.content") }}</p>
          </div>

          <div class="subsection">
            <h3>{{ t("policy.privacyPolicy.policyUpdates.title") }}</h3>
            <p>{{ t("policy.privacyPolicy.policyUpdates.content") }}</p>
          </div>
        </section>

        <!-- 联系我们 -->
        <section id="contact" class="policy-section">
          <h2>{{ t("policy.sections.contact") }}</h2>
          <p>{{ t("policy.contact.content") }}</p>
          <div class="contact-info">
            <p>
              <strong>{{ t("policy.contact.company") }}</strong>
            </p>
            <p>
              {{ t("policy.contact.email") }}
              <a href="mailto:deeptech853@gmail.com">deeptech853@gmail.com</a>
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale, tm } = useI18n();

// 静态数据
const sections = [
  { id: "introduction", title: "introduction" },
  { id: "userAgreement", title: "userAgreement" },
  { id: "privacyPolicy", title: "privacyPolicy" },
  { id: "contact", title: "contact" },
];
// 计算属性
const currentDate = computed(() => {
  return new Date().toLocaleDateString(
    locale.value == "zh_Hans"
      ? "zh-CN"
      : locale.value == "zh_Hant"
      ? "zh-Hant"
      : "en",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );
});

// 方法
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};
</script>

<style scoped>
.policy-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
  line-height: 1.6;
}

.language-selector {
  text-align: right;
  margin-bottom: 20px;
}

.language-selector select {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.policy-header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eaeaea;
}

.policy-header h1 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.policy-header p {
  color: #7f8c8d;
}

.policy-content {
  display: flex;
  gap: 30px;
}

.policy-nav {
  flex: 0 0 250px;
  position: sticky;
  top: 20px;
  align-self: flex-start;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.policy-nav h3 {
  margin-top: 0;
  color: #2c3e50;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 10px;
}

.policy-nav ul {
  list-style: none;
  padding: 0;
}

.policy-nav li {
  margin-bottom: 10px;
}

.policy-nav a {
  color: #3498db;
  text-decoration: none;
  transition: color 0.3s;
}

.policy-nav a:hover {
  color: #2980b9;
  text-decoration: underline;
}

.policy-main {
  flex: 1;
}

.policy-section {
  margin-bottom: 40px;
  padding: 25px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.policy-section h2 {
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 10px;
  margin-top: 0;
}

.subsection {
  margin-bottom: 25px;
}

.subsection h3 {
  color: #34495e;
  margin-bottom: 15px;
}

.subsection ul {
  padding-left: 20px;
}

.subsection li {
  margin-bottom: 8px;
}

.important-note {
  background: #fff8e1;
  border-left: 4px solid #ffc107;
  padding: 15px;
  margin: 20px 0;
}

.important-note h4 {
  margin-top: 0;
  color: #e65100;
}

.external-links {
  margin-top: 10px;
}

.external-links a {
  display: inline-block;
  margin-right: 15px;
  color: #3498db;
  text-decoration: none;
}

.external-links a:hover {
  text-decoration: underline;
}

.contact-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 5px;
}

.contact-info a {
  color: #3498db;
  text-decoration: none;
}

.contact-info a:hover {
  text-decoration: underline;
}

.agreement-section {
  margin-top: 40px;
  padding: 25px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  text-align: center;
}

.checkbox-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.checkbox-container input {
  margin-right: 10px;
}

.agree-button {
  background: #3498db;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.3s;
}

.agree-button:hover:not(.disabled) {
  background: #2980b9;
}

.agree-button.disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .policy-content {
    flex-direction: column;
  }

  .policy-nav {
    position: static;
    margin-bottom: 20px;
  }

  .policy-section {
    padding: 15px;
  }

  .language-selector {
    text-align: center;
  }
}
</style>
